import React from 'react';
import { trackCtaClick } from '@logging';
import { PageWrapper } from '@components/layout/page-wrapper';
import {
  Container,
  Card,
  Title,
  Section,
  SectionTitle,
  Paragraph,
  OrderedList,
  Link,
  DirectLinkBox,
  DirectLinkButton,
  ImportantNotice,
} from './delete-data-page.styles';

/**
 * Delete My Data Page
 * Allows users to request deletion of specific data categories
 * without deleting their entire account.
 * Required for Google Play Store Data Safety section.
 */
export const DeleteMyDataPage: React.FC = () => {
  const mailtoSubject = encodeURIComponent('Data Deletion Request');
  const mailtoBody = encodeURIComponent(
    'Hi,\n\nI would like to request deletion of the following data categories from my HomeCEO account:\n\n' +
    '- [ ] Profile data (name, email, address, phone number)\n' +
    '- [ ] Health data (height, weight, and related info)\n' +
    '- [ ] AI conversation history (chat logs with the AI assistant)\n' +
    '- [ ] Analytics data (usage patterns, screen views, interaction history)\n' +
    '- [ ] Diagnostic data (crash logs and performance data)\n\n' +
    'My account email: [please fill in]\n\nThank you.'
  );

  return (
    <PageWrapper contained>
      <Container>
        <Card>
          <Title>Request Data Deletion</Title>

          <Section>
            <Paragraph>
              You can request deletion of specific personal data from your HomeCEO account
              without deleting your account entirely. This page explains what data we hold,
              what you can delete yourself within the app, and how to request deletion of
              everything else.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>Data You Can Delete Yourself</SectionTitle>
            <Paragraph>
              The following data can be removed directly within the relevant section of the
              HomeCEO app:
            </Paragraph>
            <OrderedList as="ul">
              <li>Household members (adults, children, pets, vehicles)</li>
              <li>Properties and home details</li>
              <li>Calendar events and calendars</li>
              <li>Projects and tasks</li>
              <li>Research reports and saved analyses</li>
              <li>Shopping lists and items</li>
              <li>Household goals</li>
            </OrderedList>
          </Section>

          <Section>
            <SectionTitle>Data You Can Request We Delete</SectionTitle>
            <Paragraph>
              The following categories of data cannot be self-service deleted within the app.
              You can request deletion of any or all of these by contacting us:
            </Paragraph>
            <OrderedList as="ul">
              <li>
                <strong>Profile data</strong> — name, email address, and account information
              </li>
              <li>
                <strong>Health data</strong> — height, weight, and related information stored
                in growth curve reports or profile facts
              </li>
              <li>
                <strong>AI conversation history</strong> — stored chat logs and messages with
                the AI assistant
              </li>
              <li>
                <strong>Analytics data</strong> — usage patterns, screen views, feature
                interaction history, and session data
              </li>
              <li>
                <strong>Diagnostic data</strong> — crash logs, performance data, and error
                reports
              </li>
            </OrderedList>
          </Section>

          <Section>
            <SectionTitle>How to Request Deletion</SectionTitle>
            <Paragraph>
              Send an email to{' '}
              {/* Same pre-filled deletion request as the button below, so it is
                  the same decision and not one of the incidental support@
                  mentions in the legal prose (those six carry no subject or
                  body). Untracked, it made the button's number read as the
                  whole, while an unknown share of requests started here. Same
                  `cta_id` so both count toward one action; a distinct
                  `placement` so the split stays visible. */}
              <Link
                onClick={() => trackCtaClick('deletion_request_email', 'delete_my_data_inline')}
                href={`mailto:support@homeceo.ai?subject=${mailtoSubject}&body=${mailtoBody}`}
              >
                support@homeceo.ai
              </Link>{' '}
              with the subject line &quot;Data Deletion Request&quot; and specify which data
              categories you would like deleted.
            </Paragraph>

            <DirectLinkBox>
              {/* A mailto leaves no page view behind, so without this the
                  most consequential action on the site — someone actually
                  starting a deletion request — is invisible. The incidental
                  support@ mentions in the legal prose are deliberately not
                  instrumented; this is the one that is a decision. */}
              <DirectLinkButton
                onClick={() => trackCtaClick('deletion_request_email', 'delete_my_data')}
                href={`mailto:support@homeceo.ai?subject=${mailtoSubject}&body=${mailtoBody}`}
              >
                Send Deletion Request
              </DirectLinkButton>
            </DirectLinkBox>

            <Paragraph>
              Please include the email address associated with your HomeCEO account so we can
              locate your data. Requests are processed within 30 days, and you will receive an
              email confirmation when deletion is complete.
            </Paragraph>
          </Section>

          <Section>
            <SectionTitle>What Happens After Deletion</SectionTitle>
            <OrderedList as="ul">
              <li>Your HomeCEO account remains active and you can continue using the app.</li>
              <li>Deleted data cannot be recovered.</li>
              <li>
                Some data may be retained if legally required or needed for security, fraud
                prevention, or to comply with our legal obligations.
              </li>
              <li>
                Data already sent to third-party services (e.g., analytics providers) is
                subject to those services&apos; own retention and deletion policies.
              </li>
            </OrderedList>
          </Section>

          <ImportantNotice>
            <strong>Looking to delete your entire account?</strong> Visit the{' '}
            <Link href="/legal/delete-data">Delete Account</Link> page for instructions on
            full account and data removal.
          </ImportantNotice>
        </Card>
      </Container>
    </PageWrapper>
  );
};
